import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  Bell,
  BellOff,
  ArrowLeft,
  CheckCheck,
  Loader2,
  Briefcase,
  Star,
  PartyPopper,
  AlertCircle,
  Video,
  Eye,
  Clock,
  Settings,
  Filter,
  Trash2
} from "lucide-react";

const AllNotifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, unread, application_status
  const [markingRead, setMarkingRead] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/notifications?limit=100`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: "POST",
        credentials: "include",
      });
      
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    setMarkingRead(true);
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: "POST",
        credentials: "include",
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      toast.error("Failed to mark notifications as read");
    } finally {
      setMarkingRead(false);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.notification_id);
    }
    
    // Navigate based on notification type
    if (notification.type === "application_status" && notification.data?.job_id) {
      navigate(`/my-applications`);
    } else if (notification.type === "interview_scheduled") {
      navigate("/interviews");
    } else if (notification.type === "message") {
      navigate("/messages");
    }
  };

  const getNotificationIcon = (notification) => {
    const status = notification.data?.new_status || notification.type;
    
    const iconMap = {
      shortlisted: { icon: Star, color: "text-yellow-500", bg: "bg-yellow-500/20" },
      offered: { icon: PartyPopper, color: "text-green-500", bg: "bg-green-500/20" },
      hired: { icon: PartyPopper, color: "text-emerald-500", bg: "bg-emerald-500/20" },
      rejected: { icon: AlertCircle, color: "text-red-500", bg: "bg-red-500/20" },
      reviewed: { icon: Eye, color: "text-blue-500", bg: "bg-blue-500/20" },
      interviewed: { icon: Video, color: "text-purple-500", bg: "bg-purple-500/20" },
      interview_scheduled: { icon: Video, color: "text-purple-500", bg: "bg-purple-500/20" },
      application_status: { icon: Briefcase, color: "text-crimson", bg: "bg-crimson/20" },
    };
    
    return iconMap[status] || { icon: Bell, color: "text-gray-400", bg: "bg-gray-500/20" };
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === "unread") return !n.read;
    if (filter === "application_status") return n.type === "application_status";
    return true;
  });

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-obsidian" data-testid="all-notifications-page">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Link 
                to="/dashboard/jobseeker"
                className="p-2 hover:bg-white/5 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-400" />
              </Link>
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                  NOTIFICATIONS
                </h1>
                {unreadCount > 0 && (
                  <p className="text-gray-500 text-sm">{unreadCount} unread</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  onClick={markAllAsRead}
                  disabled={markingRead}
                  variant="outline"
                  className="border-[#333] text-white hover:border-crimson"
                  data-testid="mark-all-read-btn"
                >
                  {markingRead ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCheck className="w-4 h-4 mr-2" />
                  )}
                  Mark all read
                </Button>
              )}
              <Link to="/notifications/settings">
                <Button variant="outline" className="border-[#333] text-white hover:border-crimson">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Button>
              </Link>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
            {[
              { key: "all", label: "All" },
              { key: "unread", label: "Unread" },
              { key: "application_status", label: "Applications" }
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  filter === f.key 
                    ? 'bg-crimson text-white' 
                    : 'bg-obsidian-light border border-[#333] text-gray-400 hover:border-[#444]'
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Notifications List */}
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-crimson" />
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center bg-obsidian-light border border-[#222]">
              <BellOff className="w-16 h-16 text-gray-600 mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">
                {filter === "all" ? "No notifications yet" : `No ${filter.replace("_", " ")} notifications`}
              </h3>
              <p className="text-gray-500 text-sm max-w-sm">
                {filter === "all" 
                  ? "We'll notify you when there's activity on your applications, interviews, and more."
                  : "Try changing the filter to see more notifications."}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredNotifications.map((notification, index) => {
                const iconConfig = getNotificationIcon(notification);
                const Icon = iconConfig.icon;
                
                return (
                  <motion.div
                    key={notification.notification_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 bg-obsidian-light border border-[#222] hover:border-[#333] cursor-pointer transition-colors ${
                      !notification.read ? 'border-l-2 border-l-crimson' : ''
                    }`}
                    data-testid={`notification-item-${notification.notification_id}`}
                  >
                    <div className="flex gap-4">
                      <div className={`w-12 h-12 rounded-full ${iconConfig.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-6 h-6 ${iconConfig.color}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className={`text-sm ${!notification.read ? 'text-white font-semibold' : 'text-gray-300'}`}>
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <span className="w-2 h-2 bg-crimson rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-500 mt-1">
                          {notification.message}
                        </p>
                        
                        {notification.data?.note && (
                          <div className="mt-2 p-2 bg-obsidian border border-[#333] text-xs text-gray-400 italic">
                            "{notification.data.note}"
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 mt-2">
                          <Clock className="w-3 h-3 text-gray-600" />
                          <span className="text-xs text-gray-600">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AllNotifications;
